import { type ReactNode, useEffect, useRef, useState } from "react";

import styles from "./DiffTab.module.css";
import { PrPanel } from "./PrPanel";
import { type HighlightedSources, highlightSources } from "./syntaxHighlight";
import { languageForPath } from "./syntaxLanguages";
import { scrollFrameOf, useDiffScrollAnchor } from "./useDiffScrollAnchor";
import { useSession } from "../../auth/session";
import { Avatar } from "../../components/Avatar";
import { Button } from "../../components/Button";
import { Checkbox } from "../../components/Checkbox";
import { EmptyState } from "../../components/EmptyState";
import {
  addDiffComment,
  type ApiDiffComment,
  deleteDiffComment,
  type DiffSide,
  patchDiffComment,
  useDiffComments,
} from "../../data/api/diffComments";
import {
  type DiffFileView,
  type LiveDiffFrame,
  useTaskDiff,
  useTaskDiffFile,
} from "../../data/api/diffs";
import { setFileViewed, useDiffViewed } from "../../data/api/diffViews";
import { type ApiPrComment, usePrComments } from "../../data/api/prComments";
import {
  type ReviewHistoryEntry,
  submitReview,
  useTaskReviews,
} from "../../data/api/reviews";
import { useRealtimeScope } from "../../data/realtime/useRealtimeScope";
import type { DiffLine } from "../../data/types";
import { Icon } from "../../icons";
import { APP_NAME } from "../../lib/appName";
import { cx } from "../../lib/cx";
import { relTime } from "../../lib/time";

type TreeNodeData = {
  readonly children: ReadonlyArray<TreeNodeData>;
  readonly file: DiffFileView | null;
  readonly name: string;
  readonly path: string;
};

// Build a nested tree from the flat `files[].path` list, collapsing
// single-child directory chains (`internal` → `treecache` renders as one row),
// per specifications/diffs-and-pull-requests.md §File tree.
const buildFileTree = (
  files: ReadonlyArray<DiffFileView>,
): ReadonlyArray<TreeNodeData> => {
  class Raw {
    children: Record<string, Raw> = {};
    meta: DiffFileView | null = null;
  }
  const root: Record<string, Raw> = {};
  for (const f of files) {
    const parts = f.path.split("/");
    let node = root;
    parts.forEach((p, i) => {
      const existing = node[p] ?? new Raw();
      node[p] = existing;
      if (i === parts.length - 1) {
        existing.meta = f;
      }
      node = existing.children;
    });
  }
  const toArr = (
    obj: Record<string, Raw>,
    prefix: string,
  ): Array<TreeNodeData> =>
    Object.entries(obj).map(([name, v]) => {
      const path = prefix ? `${prefix}/${name}` : name;
      const children = toArr(v.children, path);
      return {
        children,
        file: v.meta && children.length === 0 ? v.meta : null,
        name,
        path,
      };
    });
  const collapse = (nodes: ReadonlyArray<TreeNodeData>): Array<TreeNodeData> =>
    nodes.map((n) => {
      let node = n;
      while (
        !node.file &&
        node.children.length === 1 &&
        node.children[0] &&
        !node.children[0].file
      ) {
        const child = node.children[0];
        node = { ...child, name: `${node.name}/${child.name}` };
      }
      return { ...node, children: collapse(node.children) };
    });
  return collapse(toArr(root, ""));
};

const renderLine = (
  l: DiffLine,
  i: number,
  syntaxTokens: ReadonlyArray<{
    readonly color?: string;
    readonly content: string;
  }> | null,
  onComment?: () => void,
): ReactNode => {
  if (l.type === "head") {
    return (
      <div className={styles.hunkHead} key={i}>
        {l.text}
      </div>
    );
  }
  const sign = l.type === "add" ? "+" : l.type === "del" ? "−" : " ";
  const numbers = (
    <>
      <span className={styles.ln}>{l.n1 ?? ""}</span>
      <span className={styles.ln}>{l.n2 ?? ""}</span>
    </>
  );
  const tokenText = syntaxTokens?.map((token) => token.content).join("");
  const source =
    syntaxTokens && tokenText === (l.src ?? "")
      ? syntaxTokens.map((token, tokenIndex) => (
          <span
            data-syntax-token
            key={tokenIndex}
            style={token.color ? { color: token.color } : undefined}
          >
            {token.content}
          </span>
        ))
      : l.src;
  return (
    <div className={cx(styles.line, styles[l.type])} key={i}>
      {/* The gutter is the comment target — the whole 88px of it, which is a
          real thing to aim at. It used to be a 20px button at the far right of
          the row, invisible until the pointer was already over that exact row:
          discoverable only if you knew it was there. */}
      {onComment ? (
        <button
          aria-label={`Comment on line ${(l.n2 ?? l.n1 ?? 0).toString()}`}
          className={styles.gutter}
          onClick={onComment}
          title="Comment on this line"
          type="button"
        >
          {numbers}
          <span aria-hidden className={styles.gutterPlus}>
            +
          </span>
        </button>
      ) : (
        numbers
      )}
      <span className={styles.src}>
        <span className={styles.sign}>{sign}</span>
        {source}
      </span>
    </div>
  );
};

export type DiffTabProps = {
  readonly taskId: string;
};

export const DiffTab = ({ taskId }: DiffTabProps) => {
  const { user } = useSession();
  const {
    applyFrame,
    diff,
    error,
    loading,
    refetch: refetchDiff,
  } = useTaskDiff(taskId);
  const { comments, refetch: refetchComments } = useDiffComments(taskId);
  const { comments: remoteComments, refetch: refetchRemote } =
    usePrComments(taskId);
  const { history, pending, refetch: refetchReviews } = useTaskReviews(taskId);
  const {
    changedPaths,
    refetch: refetchViewed,
    respondedPaths,
    viewedPaths,
  } = useDiffViewed(taskId);

  // A new snapshot supersedes the rendered diff; a comment event (a reply, a
  // resolve, an edit from any viewer) changes the threads. Reload the affected
  // overlays.
  useRealtimeScope(`task:${taskId}`, (event) => {
    if (event.kind === "diff.snapshot.created" || event.kind === "resync") {
      refetchDiff();
      refetchComments();
      refetchReviews();
      refetchRemote();
      refetchViewed();
    } else if (event.kind === "diff.live.updated") {
      // A revision of the turn in flight. Applied to what is already held
      // rather than refetched: the five-request fan-out above is right once a
      // turn and indefensible every couple of seconds, per open tab. The
      // overlays it would reload cannot have moved either — comments and
      // viewed marks bind to the last *sealed* snapshot, which an open
      // revision does not touch.
      applyFrame(event.summary as LiveDiffFrame);
    } else if (event.kind === "pull_request.updated") {
      // A PR sync just refreshed the PR — it may have mirrored fresh reviewer
      // comments from the provider. Pull those in (the local overlays are
      // untouched).
      refetchRemote();
    } else if (
      event.kind === "diff.comment.added" ||
      event.kind === "diff.comment.updated" ||
      event.kind === "diff.comment.removed" ||
      // A collaborator submitted a review round: its draft comments just became
      // published, so pull them in along with the updated review history.
      event.kind === "task_review.submitted"
    ) {
      refetchComments();
      refetchReviews();
      refetchViewed();
    }
  });
  const [viewed, setViewed] = useState<ReadonlySet<string>>(new Set());
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const seededSnapshot = useRef<string | null>(null);

  // Marking a file viewed also collapses its card; un-viewing re-expands it
  // (§Viewed state). Shared by the local toggle and by the same user's other
  // tabs, so a file ticked off in one looks identical in both.
  const applyViewed = (path: string, on: boolean): void => {
    const toggle = (set: ReadonlySet<string>): ReadonlySet<string> => {
      const next = new Set(set);
      if (on) {
        next.add(path);
      } else {
        next.delete(path);
      }
      return next;
    };
    setViewed(toggle);
    setCollapsed(toggle);
  };

  // The reader's own other tabs (`realtime.md` §Per-file viewed state). Viewed
  // state is per-user, which is why this rides the `user` scope rather than the
  // task's — and why a tab open on a different task ignores it.
  useRealtimeScope(user ? `user:${user.public_id}` : null, (event) => {
    if (event.kind !== "diff.file_view.updated") {
      return;
    }
    const summary = (event.summary ?? {}) as {
      path?: unknown;
      task_public_id?: unknown;
      viewed?: unknown;
    };
    if (summary.task_public_id === taskId && typeof summary.path === "string") {
      applyViewed(summary.path, summary.viewed === true);
    }
  });

  // Seed the viewed set from the server, re-seeding whenever the snapshot
  // changes: the server has already dropped the mark from any file that moved
  // (now surfaced as "changed"), so this reconciles the local set to the new
  // head. Seeding mutable UI state from async server data is the sanctioned
  // use of setState-in-effect.
  useEffect(() => {
    const snapId = diff?.snapshotId ?? null;
    if (
      snapId &&
      viewedPaths !== undefined &&
      seededSnapshot.current !== snapId
    ) {
      seededSnapshot.current = snapId;
      setViewed(new Set(viewedPaths));
      setCollapsed((c) => new Set([...c, ...viewedPaths]));
    }
  }, [diff?.snapshotId, viewedPaths]);
  const [hideWs, setHideWs] = useState(false);
  const [hideReviewed, setHideReviewed] = useState(false);
  const [follow, setFollow] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  // A moving diff must not slide the passage somebody is reading out from
  // under them: Follow keeps the file the agent just wrote in view, and with it
  // off the card the reader was on stays exactly where it was.
  useDiffScrollAnchor(
    scrollRef,
    diff?.revision ?? 0,
    follow && diff && !diff.sealed ? diff.lastTouchedPath : null,
  );

  if (loading && !diff) {
    return <div className={styles.state}>Loading diff…</div>;
  }
  if (error || !diff) {
    return (
      <EmptyState
        body="The diff couldn't be loaded. Try again in a moment."
        title="Diff unavailable"
      />
    );
  }
  if (!diff.snapshotId || diff.files.length === 0) {
    return (
      <EmptyState
        body="This task hasn't produced any changes yet."
        title="No diff yet"
      />
    );
  }

  // The toggle is optimistic: local state updates immediately and the PUT
  // persists it per-user; a failed write is swallowed (the set re-seeds from
  // the server on next mount). The write's own `diff.file_view.updated` comes
  // back on the user scope and applies the same change again, idempotently —
  // it is the reader's *other* tabs it exists for.
  const markViewed = (path: string, on: boolean): void => {
    void setFileViewed(taskId, path, on).catch(() => undefined);
    applyViewed(path, on);
  };
  const toggleCollapse = (path: string): void =>
    setCollapsed((c) => {
      const n = new Set(c);
      if (n.has(path)) {
        n.delete(path);
      } else {
        n.add(path);
      }
      return n;
    });
  const scrollToFile = (path: string): void => {
    const card = scrollRef.current?.querySelector<HTMLElement>(
      `[data-file="${CSS.escape(path)}"]`,
    );
    if (!card) {
      return;
    }
    const frame = scrollFrameOf(card);
    frame.scrollTop +=
      card.getBoundingClientRect().top - frame.getBoundingClientRect().top - 8;
  };
  // A draft add/remove changes the pending count, so reload both the comments
  // and the review summary together.
  const reloadComments = (): void => {
    refetchComments();
    refetchReviews();
  };
  const draftIds = comments
    .filter((c) => c.status === "draft")
    .map((c) => c.public_id);
  const submitReviewRound = (summary: string): Promise<void> =>
    submitReview(taskId, { commentIds: draftIds, summary }).then(
      reloadComments,
    );

  // "Changed since viewed": server-flagged paths whose diff moved since the
  // user last reviewed them. "Responded" (agent replies since the last round)
  // takes precedence. A file the user has since re-viewed locally clears both.
  const responded = new Set(
    (respondedPaths ?? []).filter((p) => !viewed.has(p)),
  );
  const changed = new Set(
    (changedPaths ?? []).filter((p) => !viewed.has(p) && !responded.has(p)),
  );

  const visibleFiles = diff.files.filter(
    (f) => !(hideReviewed && viewed.has(f.path)),
  );
  const tree = buildFileTree(visibleFiles);

  return (
    <div className={styles.pane}>
      <div className={styles.files}>
        <div className={styles.filesHead}>
          <span>Files</span>
          <span className={styles.filesHeadCount}>
            {viewed.size}/{diff.files.length}
          </span>
        </div>
        <div className={styles.tree}>
          {tree.map((node) => (
            <TreeNode
              isChanged={(p) => changed.has(p)}
              isResponded={(p) => responded.has(p)}
              isViewed={(p) => viewed.has(p)}
              key={node.path}
              node={node}
              onPick={scrollToFile}
            />
          ))}
        </div>
      </div>

      <div className={styles.view}>
        <PrPanel taskId={taskId} />
        <div className={styles.toolbar}>
          <Checkbox checked={hideWs} onCheckedChange={setHideWs}>
            Hide whitespace
          </Checkbox>
          <Checkbox checked={hideReviewed} onCheckedChange={setHideReviewed}>
            Hide reviewed
          </Checkbox>
          {diff.sealed ? null : (
            <>
              {/* A diff that moves under a reader with nothing on screen saying
                  so reads as a rendering bug. */}
              <span className={styles.live}>
                <span aria-hidden className={styles.liveDot} />
                <span>Live — updating as Tinsmith works</span>
              </span>
              <Checkbox checked={follow} onCheckedChange={setFollow}>
                Follow
              </Checkbox>
            </>
          )}
          <span className={styles.grow} />
          <span className={styles.summary}>
            <span className={cx(styles.delta, styles.statAdd)}>
              +{diff.linesAdded}
            </span>
            <span className={cx(styles.delta, styles.statDel)}>
              −{diff.linesRemoved}
            </span>
          </span>
        </div>

        <div className={styles.scroll} ref={scrollRef}>
          {visibleFiles.length === 0 ? (
            <div className={styles.allviewed}>
              <Icon.Check size={20} /> All files reviewed.
            </div>
          ) : (
            visibleFiles.map((f) => (
              <DiffFileCard
                changed={changed.has(f.path)}
                collapsed={collapsed.has(f.path)}
                comments={comments.filter((c) => c.path === f.path)}
                currentUserId={user?.public_id ?? null}
                editing={diff.touchedPaths.has(f.path)}
                file={f}
                hideWs={hideWs}
                key={f.path}
                onCommentsChanged={reloadComments}
                onToggleCollapse={toggleCollapse}
                onToggleViewed={markViewed}
                remoteComments={remoteComments.filter((c) => c.path === f.path)}
                responded={responded.has(f.path)}
                snapshotId={diff.reviewSnapshotId}
                taskId={taskId}
                viewed={viewed.has(f.path)}
              />
            ))
          )}
        </div>

        <ReviewBar
          history={history}
          onSubmit={submitReviewRound}
          pending={pending}
        />
      </div>
    </div>
  );
};

type ReviewBarProps = {
  readonly history: ReadonlyArray<ReviewHistoryEntry>;
  readonly onSubmit: (summary: string) => Promise<void>;
  readonly pending: number;
};

// The batched-review footer (§Code review): shows the pending draft count, an
// expandable history of submitted rounds, and "Start review" → a summary field
// that submits the round, publishing the caller's drafts to the agent.
const ReviewBar = ({ history, onSubmit, pending }: ReviewBarProps) => {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const lastRound = history[0]?.round ?? null;

  const submit = (): void => {
    if (busy || pending === 0) {
      return;
    }
    setBusy(true);
    void onSubmit(summary.trim())
      .then(() => {
        setSummary("");
        setOpen(false);
      })
      .catch(() => undefined)
      .finally(() => setBusy(false));
  };

  return (
    <div className={styles.reviewWrap}>
      {showHistory && history.length > 0 ? (
        <div className={styles.reviewHistory}>
          {history.map((r) => (
            <div className={styles.reviewHistoryRow} key={r.public_id}>
              <span className={styles.reviewHistoryRound}>Round {r.round}</span>
              <div className={styles.reviewHistoryBody}>
                <div className={styles.reviewHistoryMeta}>
                  {r.comment_count}{" "}
                  {r.comment_count === 1 ? "comment" : "comments"}
                  {r.submitted_by_name ? ` · ${r.submitted_by_name}` : ""}
                  {r.submitted_at
                    ? ` · ${relTime(Date.parse(r.submitted_at))}`
                    : ""}
                </div>
                {r.summary ? (
                  <div className={styles.reviewHistorySummary}>{r.summary}</div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div className={styles.reviewBar}>
        <div className={styles.reviewInfo}>
          <span className={styles.reviewPending}>
            {pending} pending {pending === 1 ? "comment" : "comments"}
          </span>
          {lastRound !== null ? (
            <button
              className={styles.reviewRound}
              onClick={() => setShowHistory((s) => !s)}
              type="button"
            >
              {history.length} {history.length === 1 ? "review" : "reviews"} ·
              latest round {lastRound}
              <Icon.ChevDown
                size={11}
                style={{
                  transform: showHistory ? "rotate(180deg)" : "none",
                  transition: "transform .12s",
                }}
              />
            </button>
          ) : null}
        </div>
        {open ? (
          <div className={styles.reviewCompose}>
            <textarea
              autoFocus
              className={styles.reviewSummary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Overall summary (optional)…"
              value={summary}
            />
            <Button onClick={() => setOpen(false)} size="sm" variant="ghost">
              Cancel
            </Button>
            <Button
              disabled={busy || pending === 0}
              onClick={submit}
              size="sm"
              variant="accent"
            >
              Submit review
            </Button>
          </div>
        ) : (
          <Button
            onClick={() => setOpen(true)}
            size="sm"
            variant={pending > 0 ? "accent" : "ghost"}
          >
            {history.length > 0 ? "Start another review" : "Start review"}
          </Button>
        )}
      </div>
    </div>
  );
};

const anchorOf = (l: DiffLine): { line: number; side: DiffSide } | null => {
  if (l.type === "head") {
    return null;
  }
  if (l.type === "del") {
    return l.n1 == null ? null : { line: l.n1, side: "old" };
  }
  return l.n2 == null ? null : { line: l.n2, side: "new" };
};

const tokensForLine = (
  line: DiffLine,
  highlighted: HighlightedSources,
): ReadonlyArray<{
  readonly color?: string;
  readonly content: string;
}> | null => {
  if (line.type === "head") {
    return null;
  }
  if (line.type === "del") {
    return line.n1 ? (highlighted.oldTokens?.[line.n1 - 1] ?? null) : null;
  }
  if (line.n2) {
    const fromNew = highlighted.newTokens?.[line.n2 - 1];
    if (fromNew) {
      return fromNew;
    }
  }
  return line.n1 ? (highlighted.oldTokens?.[line.n1 - 1] ?? null) : null;
};

type HighlightedState = HighlightedSources & {
  readonly newSource: string | null;
  readonly oldSource: string | null;
  readonly path: string;
};

const EMPTY_HIGHLIGHTED: HighlightedSources = {
  newTokens: null,
  oldTokens: null,
};

type DiffFileCardProps = {
  readonly changed: boolean;
  readonly collapsed: boolean;
  readonly comments: ReadonlyArray<ApiDiffComment>;
  readonly currentUserId: string | null;
  /**
   * The open turn has moved this file since the review anchor. Its lines are
   * being rewritten as the composer would sit over them, so the gutter offers
   * nothing (§Diff comments); every other file's composer is untouched.
   */
  readonly editing: boolean;
  readonly file: DiffFileView;
  readonly hideWs: boolean;
  readonly onCommentsChanged: () => void;
  readonly onToggleCollapse: (path: string) => void;
  readonly onToggleViewed: (path: string, on: boolean) => void;
  readonly remoteComments: ReadonlyArray<ApiPrComment>;
  readonly responded: boolean;
  readonly snapshotId: string | null;
  readonly taskId: string;
  readonly viewed: boolean;
};

const DiffFileCard = ({
  changed,
  collapsed,
  comments,
  currentUserId,
  editing,
  file,
  hideWs,
  onCommentsChanged,
  onToggleCollapse,
  onToggleViewed,
  remoteComments,
  responded,
  snapshotId,
  taskId,
  viewed,
}: DiffFileCardProps) => {
  const { error, lines, loading, newSource, oldSource, oversized, patch } =
    useTaskDiffFile(taskId, file.path, hideWs, file.contentSha);
  const [highlightedState, setHighlightedState] = useState<HighlightedState>({
    newSource: null,
    newTokens: null,
    oldSource: null,
    oldTokens: null,
    path: "",
  });
  const [composerAt, setComposerAt] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const language = languageForPath(file.path);
    if (!language || (oldSource === null && newSource === null)) {
      return;
    }
    let active = true;
    void highlightSources(language, oldSource, newSource)
      .then((result) => {
        if (active) {
          setHighlightedState({
            ...result,
            newSource,
            oldSource,
            path: file.path,
          });
        }
      })
      .catch(() => {
        // Highlighting is an enhancement. Unsupported/broken grammars and
        // worker failures keep the existing safe plaintext diff.
      });
    return () => {
      active = false;
    };
  }, [file.path, newSource, oldSource]);
  const highlighted =
    highlightedState.path === file.path &&
    highlightedState.oldSource === oldSource &&
    highlightedState.newSource === newSource
      ? highlightedState
      : EMPTY_HIGHLIGHTED;

  // Replies to a given top-level comment, gathered from the file's comment set.
  const repliesTo = (parentId: string): ReadonlyArray<ApiDiffComment> =>
    comments.filter((r) => r.parent_public_id === parentId);

  const submit = (side: DiffSide, line: number): void => {
    if (!draft.trim() || busy || !snapshotId) {
      return;
    }
    setBusy(true);
    void addDiffComment(taskId, {
      body: draft.trim(),
      line,
      path: file.path,
      side,
      snapshotId,
    })
      .then(() => {
        setDraft("");
        setComposerAt(null);
        onCommentsChanged();
      })
      .catch(() => undefined)
      .finally(() => setBusy(false));
  };

  const downloadPatch = (): void => {
    const url = URL.createObjectURL(new Blob([patch], { type: "text/plain" }));
    const a = document.createElement("a");
    a.download = `${file.path.split("/").pop() ?? "file"}.diff`;
    a.href = url;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={styles.filecard} data-file={file.path}>
      <div className={styles.filecardHead}>
        <button
          className={styles.collapse}
          onClick={() => onToggleCollapse(file.path)}
          title={collapsed ? "Expand" : "Collapse"}
          type="button"
        >
          <Icon.ChevDown
            size={13}
            style={{
              transform: collapsed ? "rotate(-90deg)" : "none",
              transition: "transform .12s",
            }}
          />
        </button>
        <span className={styles.filecardName}>{file.path}</span>
        {editing ? (
          <span
            className={cx(styles.badge, styles.since, styles.editing)}
            title="Tinsmith is editing this file — you can review it when the turn ends"
          >
            editing
          </span>
        ) : null}
        {responded ? (
          <span
            className={cx(styles.badge, styles.since, styles.responded)}
            title="Tinsmith responded — re-review"
          >
            responded
          </span>
        ) : changed ? (
          <span
            className={cx(styles.badge, styles.since, styles.changed)}
            title="Changed since you last viewed"
          >
            changed
          </span>
        ) : null}
        {comments.length + remoteComments.length > 0 ? (
          <span className={styles.commentCount}>
            <Icon.Comment size={11} /> {comments.length + remoteComments.length}
          </span>
        ) : null}
        <span className={cx(styles.delta, styles.statAdd)}>
          +{file.linesAdded}
        </span>
        <span className={cx(styles.delta, styles.statDel)}>
          −{file.linesRemoved}
        </span>
        <span className={styles.grow} />
        <Checkbox
          checked={viewed}
          onCheckedChange={(v) => onToggleViewed(file.path, v)}
          tone="done"
        >
          Viewed
        </Checkbox>
      </div>
      {!collapsed ? (
        <div>
          {loading ? (
            <div className={styles.hunkHead}>Loading diff…</div>
          ) : null}
          {error ? (
            <div className={styles.hunkHead}>
              This file&apos;s diff couldn&apos;t be loaded.
            </div>
          ) : null}
          {oversized ? (
            <div className={styles.oversized}>
              <p className={styles.oversizedTitle}>
                This diff is over 500&nbsp;KB — too large to render inline.
              </p>
              <Button onClick={downloadPatch} size="sm" variant="accent">
                <Icon.ArrowDown size={13} /> Download to view
              </Button>
            </div>
          ) : !loading && !error ? (
            lines.map((l, i) => {
              const anchor = anchorOf(l);
              const key = anchor ? `${anchor.side}:${anchor.line}` : null;
              const node = renderLine(
                l,
                i,
                tokensForLine(l, highlighted),
                // No composer while the turn is rewriting this file: a comment
                // anchors to a line number, and the line under the cursor is
                // one the agent is in the middle of moving.
                key === null || editing
                  ? undefined
                  : () => {
                      setComposerAt(key);
                      setDraft("");
                    },
              );
              if (!anchor || key === null) {
                return node;
              }
              // Top-level comments anchored to this line (replies thread under
              // their parent, so they're excluded here).
              const lineComments = comments.filter(
                (c) =>
                  c.side === anchor.side &&
                  c.line === anchor.line &&
                  !c.parent_public_id,
              );
              const lineRemote = remoteComments.filter(
                (c) => c.side === anchor.side && c.line === anchor.line,
              );
              return (
                <div key={i}>
                  <div className={styles.lineWrap}>{node}</div>
                  {lineRemote.map((c) => (
                    <div className={styles.comment} key={c.provider_comment_id}>
                      <Avatar name={c.author_login} size={20} />
                      <div className={styles.commentBody}>
                        <div className={styles.commentMeta}>
                          {c.author_login}
                          <span className={styles.ghTag}>
                            <Icon.Github size={9} /> GitHub
                          </span>
                          · {relTime(Date.parse(c.occurred_at))}
                        </div>
                        <div className={styles.commentText}>{c.body}</div>
                      </div>
                    </div>
                  ))}
                  {lineComments.map((c) => (
                    <CommentThread
                      comment={c}
                      currentUserId={currentUserId}
                      key={c.public_id}
                      onChanged={onCommentsChanged}
                      replies={repliesTo(c.public_id)}
                      snapshotId={snapshotId}
                      taskId={taskId}
                    />
                  ))}
                  {composerAt === key ? (
                    <div className={styles.composer}>
                      <textarea
                        autoFocus
                        className={styles.composerInput}
                        onChange={(e) => setDraft(e.target.value)}
                        placeholder="Leave a comment on this line…"
                        value={draft}
                      />
                      <div className={styles.composerActions}>
                        <span className={styles.grow} />
                        <Button
                          onClick={() => setComposerAt(null)}
                          size="sm"
                          variant="ghost"
                        >
                          Cancel
                        </Button>
                        <Button
                          disabled={!draft.trim() || busy}
                          onClick={() => submit(anchor.side, anchor.line)}
                          size="sm"
                          variant="accent"
                        >
                          Comment
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

// One top-level comment plus its threaded replies, with in-place edit, reply,
// resolve, and remove. See §Diff comments / §Code review.
type CommentThreadProps = {
  readonly comment: ApiDiffComment;
  readonly currentUserId: string | null;
  readonly onChanged: () => void;
  readonly replies: ReadonlyArray<ApiDiffComment>;
  readonly snapshotId: string | null;
  readonly taskId: string;
};

const CommentThread = ({
  comment,
  currentUserId,
  onChanged,
  replies,
  snapshotId,
  taskId,
}: CommentThreadProps) => {
  const [editing, setEditing] = useState(false);
  const [replying, setReplying] = useState(false);
  const [editText, setEditText] = useState(comment.body);
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState(false);
  const mine =
    currentUserId != null && comment.author_public_id === currentUserId;
  const resolved = comment.status === "resolved";
  const draft = comment.status === "draft";

  const act = (p: Promise<unknown>): void => {
    setBusy(true);
    void p
      .then(onChanged)
      .catch(() => undefined)
      .finally(() => setBusy(false));
  };
  const saveEdit = (): void => {
    if (!editText.trim() || busy) {
      return;
    }
    act(
      patchDiffComment(taskId, comment.public_id, {
        body: editText.trim(),
      }).then(() => setEditing(false)),
    );
  };
  const postReply = (): void => {
    if (!replyText.trim() || busy || !snapshotId) {
      return;
    }
    act(
      addDiffComment(taskId, {
        body: replyText.trim(),
        line: comment.line,
        parentCommentId: comment.public_id,
        path: comment.path,
        side: comment.side,
        snapshotId,
      }).then(() => {
        setReplyText("");
        setReplying(false);
      }),
    );
  };

  return (
    <div className={styles.thread}>
      <div className={cx(styles.comment, resolved && styles.resolved)}>
        <Avatar name={comment.display_name ?? "You"} size={20} />
        <div className={styles.commentBody}>
          <div className={styles.commentMeta}>
            {comment.display_name ?? "You"} ·{" "}
            {relTime(Date.parse(comment.occurred_at))}
            {draft ? " · draft" : ""}
            {resolved ? (
              <span className={styles.resolvedTag}>resolved</span>
            ) : null}
            {comment.outdated ? (
              <span
                className={styles.outdatedTag}
                title="The file moved since this was written"
              >
                outdated
              </span>
            ) : null}
          </div>
          {editing ? (
            <div className={styles.composer}>
              <textarea
                autoFocus
                className={styles.composerInput}
                onChange={(e) => setEditText(e.target.value)}
                value={editText}
              />
              <div className={styles.composerActions}>
                <span className={styles.grow} />
                <Button
                  onClick={() => {
                    setEditing(false);
                    setEditText(comment.body);
                  }}
                  size="sm"
                  variant="ghost"
                >
                  Cancel
                </Button>
                <Button
                  disabled={!editText.trim() || busy}
                  onClick={saveEdit}
                  size="sm"
                  variant="accent"
                >
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className={styles.commentText}>{comment.body}</div>
              <div className={styles.commentActions}>
                {!draft ? (
                  <button
                    className={styles.commentAction}
                    onClick={() => setReplying((r) => !r)}
                    type="button"
                  >
                    Reply
                  </button>
                ) : null}
                {mine ? (
                  <button
                    className={styles.commentAction}
                    onClick={() => {
                      setEditing(true);
                      setEditText(comment.body);
                    }}
                    type="button"
                  >
                    Edit
                  </button>
                ) : null}
                {/* Resolving is the author's call, like editing and removing;
                    the server rejects anyone else below `admin`. */}
                {!draft && mine ? (
                  <button
                    className={styles.commentAction}
                    onClick={() =>
                      act(
                        patchDiffComment(taskId, comment.public_id, {
                          status: resolved ? "published" : "resolved",
                        }),
                      )
                    }
                    type="button"
                  >
                    {resolved ? "Reopen" : "Resolve"}
                  </button>
                ) : null}
                {mine ? (
                  <button
                    className={cx(styles.commentAction, styles.danger)}
                    onClick={() =>
                      act(deleteDiffComment(taskId, comment.public_id))
                    }
                    type="button"
                  >
                    Remove
                  </button>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
      {replies.map((r) => (
        <div className={cx(styles.comment, styles.reply)} key={r.public_id}>
          <Avatar name={r.display_name ?? APP_NAME} size={18} />
          <div className={styles.commentBody}>
            <div className={styles.commentMeta}>
              {r.display_name ?? APP_NAME} replied ·{" "}
              {relTime(Date.parse(r.occurred_at))}
            </div>
            <div className={styles.commentText}>{r.body}</div>
          </div>
        </div>
      ))}
      {replying ? (
        <div className={cx(styles.composer, styles.reply)}>
          <textarea
            autoFocus
            className={styles.composerInput}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Reply…"
            value={replyText}
          />
          <div className={styles.composerActions}>
            <span className={styles.grow} />
            <Button
              onClick={() => {
                setReplying(false);
                setReplyText("");
              }}
              size="sm"
              variant="ghost"
            >
              Cancel
            </Button>
            <Button
              disabled={!replyText.trim() || busy}
              onClick={postReply}
              size="sm"
              variant="accent"
            >
              Reply
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

type TreeNodeProps = {
  readonly depth?: number;
  readonly isChanged: (path: string) => boolean;
  readonly isResponded: (path: string) => boolean;
  readonly isViewed: (path: string) => boolean;
  readonly node: TreeNodeData;
  readonly onPick: (path: string) => void;
};

const TreeNode = ({
  depth = 0,
  isChanged,
  isResponded,
  isViewed,
  node,
  onPick,
}: TreeNodeProps) => {
  const [open, setOpen] = useState(true);
  if (node.file) {
    const filePath = node.file.path;
    const isFileViewed = isViewed(filePath);
    return (
      <button
        className={cx(styles.treeFile, isFileViewed && styles.viewed)}
        onClick={() => onPick(filePath)}
        style={{ paddingLeft: 10 + depth * 14 }}
        type="button"
      >
        <Icon.GitDiff className={styles.treeIco} size={12} />
        <span className={styles.treeName}>{node.name}</span>
        {isFileViewed ? (
          <Icon.Check className={styles.treeViewed} size={11} />
        ) : isResponded(filePath) ? (
          <span
            className={cx(styles.treeDot, styles.responded)}
            title="Tinsmith responded — re-review"
          />
        ) : isChanged(filePath) ? (
          <span
            className={cx(styles.treeDot, styles.changed)}
            title="Changed since you last viewed"
          />
        ) : null}
      </button>
    );
  }
  return (
    <div>
      <button
        className={styles.treeDir}
        onClick={() => setOpen((o) => !o)}
        style={{ paddingLeft: 10 + depth * 14 }}
        type="button"
      >
        <Icon.ChevDown
          size={12}
          style={{
            transform: open ? "none" : "rotate(-90deg)",
            transition: "transform .12s",
          }}
        />
        <span className={styles.treeName}>{node.name}</span>
      </button>
      {open
        ? node.children.map((c) => (
            <TreeNode
              depth={depth + 1}
              isChanged={isChanged}
              isResponded={isResponded}
              isViewed={isViewed}
              key={c.path}
              node={c}
              onPick={onPick}
            />
          ))
        : null}
    </div>
  );
};
